import { alpha, type Theme } from '@mui/material';

export const commonStyles = {
  header: (theme: Theme) => ({
    mb: { xs: theme.spacing(3), sm: theme.spacing(4) },
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', sm: 'center' },
    gap: { xs: theme.spacing(1.5), sm: theme.spacing(2) },
    flexWrap: 'wrap',
  }),
  subtitle: (theme: Theme) => ({
    mt: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
  }),
  content: (theme: Theme) => ({
    width: '100%',
    minWidth: 0,
    mt: { xs: theme.spacing(3), sm: theme.spacing(4) },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: theme.spacing(3), sm: theme.spacing(4) },
  }),
  loadingWrap: {
    position: 'relative',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
  },
  loadingMask: (theme: Theme) => ({
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(theme.palette.common.white, 0.56),
  }),
  backRow: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'flex-start',
    mb: theme.spacing(2),
  }),
  backButton: (theme: Theme) => ({
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: theme.spacing(1),
    alignSelf: 'flex-start',
  }),
  statusChip: (theme: Theme) => ({
    minWidth: 89,
    height: 27,
    borderRadius: '100px',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.common.white,
    '& .MuiChip-label': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      whiteSpace: 'nowrap',
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1.5),
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      lineHeight: '19px',
    },
  }),
} as const;

export const dataFormStyles = {
  documentTableSection: { backgroundColor: 'transparent' },
  documentTableToolbar: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', sm: 'center' },
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    mb: theme.spacing(2),
  }),
  tableToolbarLeft: (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  }),
  documentTableToolbarTitle: {
    fontSize: '2rem',
    lineHeight: 1.2,
    fontWeight: 700,
    color: 'text.primary',
  },
  documentPrimaryAction: (theme: Theme) => ({
    minHeight: 44,
    padding: '10px 24px',
    borderRadius: '8px',
    fontSize: '0.9375rem',
    lineHeight: 1.2,
    fontWeight: 500,
    textTransform: 'none',
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    color: theme.palette.common.white,
  }),
  documentTableContainer: (theme: Theme) => ({
    border: `1px solid ${theme.palette.border.subtle}`,
    borderRadius: '12px',
    backgroundColor: theme.palette.common.white,
    overflowX: 'auto',
  }),
  documentTable: {
    minWidth: 980,
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  documentTableHead: (theme: Theme) => ({
    backgroundColor: theme.palette.background.tableHeader,
  }),
  documentTableHeadCell: (theme: Theme, columnKey: string) => ({
    py: 1.75,
    px: 2.5,
    borderBottom: `1px solid ${theme.palette.border.subtle}`,
    backgroundColor: theme.palette.background.tableHeader,
    color: theme.palette.text.primary,
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '14px',
    lineHeight: '14px',
    fontWeight: 800,
    whiteSpace: 'nowrap',
    ...(columnKey === 'name' ? { width: '34%' } : {}),
    ...(columnKey === 'createdAt' ? { width: '28%' } : {}),
    ...(columnKey === 'status' ? { width: '20%' } : {}),
    ...(columnKey === 'actions' ? { width: '18%' } : {}),
  }),
  documentTableBodyCell: (theme: Theme, columnKey: string) => ({
    py: 2.25,
    px: 2.5,
    borderBottom: `1px solid ${alpha(theme.palette.border.subtle, 0.8)}`,
    verticalAlign: 'middle',
    color: theme.palette.text.primary,
    fontSize: '0.9375rem',
    lineHeight: '22px',
    ...(columnKey === 'actions' ? { whiteSpace: 'nowrap' } : {}),
  }),
  documentTableRow: (theme: Theme) => ({
    backgroundColor: theme.palette.common.white,
    '&:hover, &.MuiTableRow-hover:hover': {
      backgroundColor: theme.palette.common.white,
    },
    '&:last-of-type td': { borderBottom: 0 },
  }),
  documentEmptyCell: (theme: Theme) => ({
    py: theme.spacing(4),
    px: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: '0.9375rem',
    lineHeight: '22px',
    borderBottom: 0,
  }),
} as const;

export const appFormStyles = {
  pageRoot: (theme: Theme) => ({
    minHeight: '100vh',
    background: theme.palette.background.default,
    paddingTop: { xs: theme.spacing(2), md: theme.spacing(4) },
    paddingBottom: { xs: theme.spacing(3), md: theme.spacing(6) },
    fontFamily: '"Nunito Sans", sans-serif',
  }),
  paper: {
    p: 0,
    borderRadius: 0,
    border: 'none',
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  header: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1.25),
    mb: theme.spacing(1.5),
  }),
  title: (theme: Theme) => ({
    color: theme.palette.text.primary,
    fontSize: '32px',
    fontWeight: 800,
    lineHeight: '100%',
    letterSpacing: 0,
    mb: 0,
  }),
  status: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 1,
  },
  metaRow: (theme: Theme) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', sm: 'flex-end' },
    flexDirection: { xs: 'column', sm: 'row' },
    gap: theme.spacing(1),
  }),
  createdAt: (theme: Theme) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    lineHeight: '20px',
    whiteSpace: { xs: 'normal', md: 'nowrap' },
    alignSelf: { xs: 'flex-start', sm: 'flex-end' },
  }),
  actionMenuPaper: (theme: Theme) => ({
    minWidth: 196,
    border: `1px solid ${theme.palette.border.subtle}`,
    borderRadius: theme.spacing(1.25),
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.palette.shadow.menu,
    marginTop: theme.spacing(0.5),
    '& .MuiMenu-list, & .MuiMenuItem-root': {
      backgroundColor: theme.palette.common.white,
    },
  }),
  actionMenuItem: {
    minHeight: 40,
    backgroundColor: 'common.white',
    fontSize: '0.875rem',
    color: 'text.primary',
    gap: 1.5,
    '& .MuiListItemText-primary': { fontSize: '0.875rem' },
  },
  actionMenuItemDanger: {
    minHeight: 40,
    backgroundColor: 'common.white',
    fontSize: '0.875rem',
    color: 'error.main',
    gap: 1.5,
    '& .MuiListItemText-primary': { fontSize: '0.875rem' },
  },
  actionMenuItemIcon: {
    minWidth: 20,
    justifyContent: 'flex-end',
    color: 'text.primary',
  },
  actionMenuItemDangerIcon: {
    minWidth: 20,
    justifyContent: 'flex-end',
    color: 'error.main',
  },
} as const;

export const covConstantsStyles = {
  tablePrimaryText: {
    color: 'text.primary',
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '16px',
    lineHeight: '16px',
    fontWeight: 400,
    letterSpacing: 0,
  },
  tableDateText: {
    color: 'text.primary',
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '16px',
    lineHeight: '16px',
    fontWeight: 400,
    letterSpacing: 0,
  },
  covBtn: (theme: Theme) => ({
    minHeight: 36,
    padding: '8px 16px',
    borderRadius: '100px',
    borderColor: theme.palette.border.subtle,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
    lineHeight: '20px',
    fontWeight: 500,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    '&:hover': {
      borderColor: theme.palette.border.subtle,
      backgroundColor: theme.palette.primary['200'],
      color: theme.palette.text.primary,
      boxShadow: 'none',
    },
  }),
} as const;

export const covComponentsStyles = {
  statusTooltip: (theme: Theme) => ({
    position: 'relative',
    overflow: 'visible',
    maxWidth: 260,
    padding: theme.spacing(1, 1.25),
    borderRadius: theme.spacing(0.75),
    border: `1px solid ${theme.palette.border.subtle}`,
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.palette.shadow.tooltip,
    color: theme.palette.text.primary,
    fontFamily: '"Nunito Sans", sans-serif',
    fontSize: '0.8125rem',
    fontWeight: 400,
    lineHeight: '16px',
  }),
  statusInfo: (theme: Theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.grey.infoIconGrey,
    cursor: 'help',
    outline: 'none',
    transition: 'color 0.2s ease',
    '&:hover, &:focus-visible': { color: theme.palette.primary.main },
  }),
} as const;
